import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  User,
  Briefcase,
  Banknote,
  FileText,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  ClipboardCheck, // Added new icon for KYC status
} from "lucide-react";
import BasicInfoForm from "../../components/static/kyc/BasicInfoForm";
import DematDetailsForm from "../../components/static/kyc/DematDetailsForm";
import BankDetailsForm from "../../components/static/kyc/BankDetailsForm";
import DocumentUploadForm from "../../components/static/kyc/DocumentUploadForm";
import ReviewSubmission from "../../components/static/kyc/ReviewSubmission";
import ManageAccount from "../../components/static/userProfile/ManageAccount";
import KycStatusPage from "../../components/static/kyc/KYCStatus"; // Updated import path

const SidebarItem = ({ icon, label, active, onClick, isCollapsed }) => (
  <button
    onClick={onClick}
    className={`flex items-center w-full text-left px-3 py-3 rounded-xl transition-all duration-300 group relative overflow-hidden
      ${
        active
          ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30"
          : "text-gray-400 hover:bg-white/5 hover:text-white"
      }
      ${isCollapsed ? "justify-center" : ""}`}
  >
    <div className={`relative z-10 transition-transform duration-300 ${active ? "scale-110" : "group-hover:scale-110"}`}>
      {icon}
    </div>
    {!isCollapsed && (
      <span className="ml-3 text-sm font-medium relative z-10 tracking-wide">
        {label}
      </span>
    )}
    {/* Hover Glow Effect */}
    {!active && (
      <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    )}
  </button>
);

const formPathMap = {
  basicInfo: "/profile/kyc/basic-info",
  dematDetails: "/profile/kyc/demat-details",
  bankDetails: "/profile/kyc/bank-details",
  documents: "/profile/kyc/documents",
  review: "/profile/kyc/review",
   kycStatus: "/profile/kyc/status",
};

const pathFormMap = {
  "/profile/kyc/basic-info": "basicInfo",
  "/profile/kyc/demat-details": "dematDetails",
  "/profile/kyc/bank-details": "bankDetails",
  "/profile/kyc/documents": "documents",
  "/profile/kyc/review": "review",
   "/profile/kyc/status": "kycStatus",
  "/profile/kyc": "basicInfo", // Default route
};

const KycPages = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  // Get the current form from URL path or default to basicInfo
  const getCurrentFormFromPath = () => {
    const path = location.pathname;
    return pathFormMap[path] || "basicInfo";
  };

  const [currentForm, setCurrentForm] = useState(getCurrentFormFromPath());

  // Effect to sync URL with component state on initialization and URL changes
  useEffect(() => {
    const formFromPath = getCurrentFormFromPath();
    setCurrentForm(formFromPath);

    // If we're at the base KYC URL, redirect to the specific section
    if (location.pathname === "/profile/kyc") {
      navigate(formPathMap["basicInfo"], { replace: true });
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setSidebarCollapsed(false);
      } else {
        setSidebarCollapsed(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleFormClick = (formName) => {
    setCurrentForm(formName);
    navigate(formPathMap[formName]);

    if (window.innerWidth < 768) {
      setSidebarCollapsed(true);
    }
  };

  const goToNextForm = (current) => {
    const formOrder = [
      "basicInfo",
      "dematDetails",
      "bankDetails",
      "documents",
      "review",
       "kycStatus",
    ];
    const currentIndex = formOrder.indexOf(current);
    if (currentIndex !== -1 && currentIndex < formOrder.length - 1) {
      const nextForm = formOrder[currentIndex + 1];
      setCurrentForm(nextForm);
      navigate(formPathMap[nextForm]);
    }
  };

  const contentMarginClass = sidebarCollapsed
    ? "md:ml-[80px]"
    : "md:ml-[280px]";

  return (
     <div className="flex min-h-screen bg-[#f8fafc] relative py-12">
    {/* Overlay for mobile: click to close sidebar */}
    {!sidebarCollapsed && (
      <div
        className="fixed inset-0 z-20 bg-black/60 backdrop-blur-sm md:hidden"
        onClick={() => setSidebarCollapsed(true)}
        aria-label="Close sidebar"
      />
    )}
    
      {sidebarCollapsed && (
        <div className="fixed top-[14%] -translate-y-1/2 left-0 z-50 md:hidden">
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="bg-white text-gray-700 p-2 rounded-r-xl shadow-lg hover:bg-gray-50 transition-colors duration-200 border border-gray-100"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}

      <aside
        className={`bg-[#0B1120] p-4 overflow-y-auto shadow-2xl transition-all duration-300
          ${sidebarCollapsed ? "w-[80px]" : "w-72"}
          fixed md:fixed left-0 z-30
          ${
            sidebarCollapsed
              ? "-translate-x-full md:translate-x-0"
              : "translate-x-0"
          }
          md:transform-none transform top-0 h-full border-r border-white/5`}
      >
        <div className="pt-[65px] mb-8 flex justify-center">
           {/* Optional Logo or Branding here */}
           {!sidebarCollapsed && <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>}
        </div>

        <nav className="space-y-2">
          <SidebarItem
            icon={<User size={20} />}
            label="My Profile"
            active={currentForm === "basicInfo"}
            onClick={() => handleFormClick("basicInfo")}
            isCollapsed={sidebarCollapsed}
          />
          <SidebarItem
            icon={<Briefcase size={20} />}
            label="Demat Details"
            active={currentForm === "dematDetails"}
            onClick={() => handleFormClick("dematDetails")}
            isCollapsed={sidebarCollapsed}
          />
          <SidebarItem
            icon={<Banknote size={20} />}
            label="Bank Details"
            active={currentForm === "bankDetails"}
            onClick={() => handleFormClick("bankDetails")}
            isCollapsed={sidebarCollapsed}
          />
          <SidebarItem
            icon={<FileText size={20} />}
            label="Upload Documents"
            active={currentForm === "documents"}
            onClick={() => handleFormClick("documents")}
            isCollapsed={sidebarCollapsed}
          />
          <SidebarItem
            icon={<CheckCircle2 size={20} />}
            label="Review & Submit"
            active={currentForm === "review"}
            onClick={() => handleFormClick("review")}
            isCollapsed={sidebarCollapsed}
          />
          <SidebarItem
            icon={<User size={20} />}
            label="Manage Account"
            active={currentForm === "manageAccount"}
            onClick={() => handleFormClick("manageAccount")}
            isCollapsed={sidebarCollapsed}
          />
          <SidebarItem
            icon={<ClipboardCheck size={20} />}
            label="KYC Status"
            active={currentForm === "kycStatus"}
            onClick={() => handleFormClick("kycStatus")}
            isCollapsed={sidebarCollapsed}
          />
        </nav>
      </aside>

      <div className={`flex-1 ${contentMarginClass}`}>
        <main className="p-6">
          {currentForm === "basicInfo" && (
            <BasicInfoForm onComplete={() => goToNextForm("basicInfo")} />
          )}
          {currentForm === "dematDetails" && (
            <DematDetailsForm onComplete={() => goToNextForm("dematDetails")} />
          )}
          {currentForm === "bankDetails" && (
            <BankDetailsForm onComplete={() => goToNextForm("bankDetails")} />
          )}
          {currentForm === "documents" && (
            <DocumentUploadForm onComplete={() => goToNextForm("documents")} />
          )}
          {currentForm === "review" && <ReviewSubmission />}
          {currentForm === "manageAccount" && <ManageAccount />}
          {currentForm === "kycStatus" && <KycStatusPage />}{" "}
          {/* Updated component name */}
        </main>
      </div>
    </div>
  );
};

export default KycPages;
